import React from "react";
import { IEscalationMatrix } from "../../vm";
import {
  addEscalationMatrix,
  updateEscalationMatrix,
} from "../../services/escalationMatrix/EscalationMatrixService";
import {
  ITag,
  Stack,
  IStackTokens,
  Dropdown,
  DefaultButton,
  PrimaryButton,
  values,
  IDropdownOption,
} from "office-ui-fabric-react";
import CustomDrawer from "../common/CustomDrawer";
import { Formik } from "formik";
import * as yup from "yup";
import { ToastContext } from "../common/ToastProvider";
import { CustomTextField } from "../common/Library";
import { EMATRIX_LEVELS, EMATRIX_ROLES, EMATRIX_TYPES } from "../common/Constants";
import { getDatacentersForACustomer } from "../../services/customers/CustomerService";

const EmSchema = yup.object().shape({
  name: yup.string().optional(),
  email: yup.string().email().required("required"),
  phone: yup.string().required("required"),
  type: yup.string().required("required"),
  role: yup.string().required("required"),
  datacenterId: yup.string().required("required"),
  level: yup.string().required("required"),
});

export interface ManageEscalationMatrixProps {
  data?: IEscalationMatrix;
  datacenterId?: string;
  handleLoading: any;
  handleDialogclose: any;
  customerId?: string;
  eMType?: string;
}

const ManageEscalationMatrix: React.FC<ManageEscalationMatrixProps> = ({
  data,
  datacenterId,
  handleLoading,
  handleDialogclose,
  customerId,
  eMType,
}) => {
  //state
  const [state, setState] = React.useState({
    escalationMatrix: {
      name: "",
      email: "",
      phone: "",
      type: undefined,
      role: eMType ? eMType : undefined,
      datacenterId: datacenterId && datacenterId,
      level: "",
      ...data,
    },
    isLoading: false,
    defTags: [],
    datacenterOptions: [],
  } as {
    escalationMatrix: IEscalationMatrix;
    isLoading: boolean;
    defTags: ITag[];
    datacenterOptions: IDropdownOption[];
  });

  // hooks
  React.useEffect(() => {
    const asyncFunc = async () => {
      await getDcForACustomer();
    };
    asyncFunc();
  }, []);

  const getDcForACustomer = async () => {
    if (customerId !== undefined) {
      setState({ ...state, isLoading: true });
      let result = await getDatacentersForACustomer({ customerId });
      if (result && result.success) {
        let datacenterOptions: IDropdownOption[] = [];
        result.data.forEach((d) => {
          datacenterOptions.push({
            key: d._id,
            text: d.name,
          });
        });

        setState({
          ...state,
          isLoading: false,
          datacenterOptions,
        });
      } else {
        setState({
          ...state,
          isLoading: false,
        });
      }
    }
  };

  // custom hooks
  const { showToast } = React.useContext(ToastContext);

  const wrapStackTokens: IStackTokens = { childrenGap: 16 };

  const closeAddDialog = (data?: IEscalationMatrix) => {
    handleDialogclose(data);
  };

  return (
    <React.Fragment>
      <CustomDrawer
        title={
          state.escalationMatrix._id
            ? "Update Escalation Matrix"
            : "Add Escalation Matrix"
        }
        handelOpen={true}
        onClose={() => closeAddDialog()}
      >
        <Stack>
          <Formik
            enableReinitialize
            initialValues={state.escalationMatrix}
            validationSchema={EmSchema}
            onSubmit={async (values: IEscalationMatrix, { setSubmitting }) => {
              let obj = { ...values };
              let result;
              handleLoading();
              if (state.escalationMatrix._id) {
                result = await updateEscalationMatrix(obj);
              } else {
                result = await addEscalationMatrix(obj);
              }
              handleLoading();
              if (result.success) {
                closeAddDialog(result.data);
                //   this.props.toast.show(`Customer ${this.state.customer._id ? "updated" : "created"} successfully`, "success");
              } else {
              }
              setSubmitting(false);
            }}
          >
            {({ handleSubmit, setFieldValue, values, errors, touched }) => (
              <form onSubmit={handleSubmit}>
                <Stack wrap tokens={wrapStackTokens}>
                  <Stack.Item>
                    <CustomTextField name={"name"} label={"Name"} />
                  </Stack.Item>
                  <Stack.Item>
                    <CustomTextField name={"email"} label={"Email"} />
                  </Stack.Item>
                  <Stack.Item>
                    <CustomTextField name={"phone"} label={"Phone"} />
                  </Stack.Item>
                  <Stack.Item className="custom-dropdown mb-30">
                    <Dropdown
                      options={EMATRIX_ROLES}
                      selectedKey={values.role}
                      onChange={(e, item) => {
                        setFieldValue("role", item?.key);
                      }}
                      label="Function"
                      // label="Role"
                      placeholder="Select an option"
                      errorMessage={
                        (errors.role && touched.role && errors.role) as string
                      }
                      disabled={eMType !== undefined}
                      className="custom-dropdown"
                    />
                  </Stack.Item>
                  <Stack.Item className="custom-dropdown mb-30">
                    <Dropdown
                      options={EMATRIX_TYPES}
                      selectedKey={values.type}
                      onChange={(e, item) => {
                        setFieldValue("type", item?.key);
                      }}
                      label="Role"
                      // label="Type"
                      placeholder="Select an option"
                      errorMessage={
                        (errors.type && touched.type && errors.type) as string
                      }
                      className="custom-dropdown"
                    />
                  </Stack.Item>
                  {customerId !== undefined && (
                    <Stack.Item className="custom-dropdown mb-30">
                      <Dropdown
                        options={state.datacenterOptions}
                        selectedKey={values.datacenterId}
                        onChange={(e, item) => {
                          setFieldValue("datacenterId", item?.key);
                        }}
                        label="Pick a Datacenter"
                        placeholder="Select an option"
                        errorMessage={
                          (errors.datacenterId &&
                            touched.datacenterId &&
                            errors.datacenterId) as string
                        }
                      />
                    </Stack.Item>
                  )}
                  <Stack.Item className="custom-dropdown mb-30">
                    <Dropdown
                      options={EMATRIX_LEVELS}
                      selectedKey={values.level}
                      onChange={(e, item) => {
                        setFieldValue("level", item?.key);
                      }}
                      label="Level"
                      placeholder="Select an option"
                      errorMessage={
                        (errors.level && touched.level && errors.level) as string
                      }
                      className="custom-dropdown"
                    />
                  </Stack.Item>
                  <Stack.Item>
                    <Stack
                      horizontal
                      wrap
                      tokens={wrapStackTokens}
                      horizontalAlign="end"
                    >
                      <DefaultButton
                        text="Cancel"
                        onClick={() => closeAddDialog()}
                        className="borderless-btn"
                      />
                      <PrimaryButton
                        type="submit"
                        text={state.escalationMatrix._id ? "Update" : "Add"}
                      />
                    </Stack>
                  </Stack.Item>
                </Stack>
              </form>
            )}
          </Formik>
        </Stack>
      </CustomDrawer>
    </React.Fragment>
  );
};

export default ManageEscalationMatrix;
